import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AddressAutofill } from '@mapbox/search-js-react';
import { apiFetch, refreshSocketAuth, refreshSessionUser, setToken } from '../lib/api';

export default function Register() {
  const [form, setForm] = useState({
    time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    given_name: '',
    surname: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    address: '',
    city: '',
    state: '',
    postcode: '',
    country: '',
  });

  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  // Prefill email/mobile when coming from an invite deep-link
  useEffect(() => {
    try {
      const h = sessionStorage.getItem('prefill_handle');
      if (!h) return;
      sessionStorage.removeItem('prefill_handle');

      if (/@/.test(h)) {
        setForm((prev) => ({ ...prev, email: String(h).trim().toLowerCase() }));
      } else {
        setForm((prev) => ({ ...prev, mobile: String(h).trim().replace(/[^\d+]/g, '') }));
      }
    } catch {}
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAddressSelect = async (event) => {
    const feature = event.features?.[0];
    if (!feature) return;

    const props = feature.properties || {};
    const context = feature.context || [];

    const fullStreet = props.address_line1 || feature.place_name || '';
    const city = props.place || '';
    const state = props.region || '';
    const postcode = props.postcode || '';

    const countryContext = context.find(c => c.id.startsWith('country'));
    let country = form.country;
    if (countryContext?.short_code) {
      country = countryContext.short_code.toUpperCase();
    } else if (props.country) {
      country = props.country.toUpperCase();
    } else if (feature.place_name) {
      const parts = feature.place_name.split(',');
      country = parts[parts.length - 1].trim().toUpperCase();
    }

    const geometry = feature.geometry;
    let timeZone = form.time_zone;
    if (geometry?.coordinates) {
      const [lng, lat] = geometry.coordinates;
      try {
        const res = await fetch(`https://api.timezonedb.com/v2.1/get-time-zone?key=${process.env.REACT_APP_TIMEZONE_API_KEY}&format=json&by=position&lat=${lat}&lng=${lng}`);
        const data = await res.json();
        if (data?.zoneName) {
          timeZone = data.zoneName;
        }
      } catch (e) {
        console.error('Failed to fetch timezone:', e);
      }
    }

    setForm(prev => ({
      ...prev,
      address: fullStreet,
      city,
      state,
      postcode,
      country,
      time_zone: timeZone
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.given_name.trim()) newErrors.given_name = 'Given name is required';
    if (!form.surname.trim()) newErrors.surname = 'Surname is required';
    if (!form.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) newErrors.email = 'Invalid email format';
    if (!form.mobile) newErrors.mobile = 'Mobile is required';
    else if (!/^\d{8,15}$/.test(form.mobile)) newErrors.mobile = 'Enter a valid mobile number (8-15 digits)';
    if (!form.password) newErrors.password = 'Password is required';
    else if (form.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    if (!form.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    else if (form.password !== form.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const passwordStrength = () => {
    const length = form.password.length;
    if (length >= 10) return 'Strong';
    if (length >= 6) return 'Medium';
    return 'Weak';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    if (!validate()) return;

    try {
      const response = await apiFetch('/api/register', {
        method: 'POST',
        body: form,
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        if (data?.token) {
          setToken(data.token);
          refreshSocketAuth();
        } else {
          setMessage('❌ Registration failed: no token returned');
          return;
        }

        const me = await refreshSessionUser().catch(() => null);
        const user = (me && me.user) ? me.user : (data.user || data.me || null);
        if (user) sessionStorage.setItem('user', JSON.stringify(user));

        const pendingInvite = sessionStorage.getItem('pending_invite');
        if (pendingInvite) {
          sessionStorage.removeItem('pending_invite');
          navigate(`/invite/${pendingInvite}`);
        } else {
          navigate('/');
        }
      } else {
        setMessage(`❌ ${data.error || 'Registration failed.'}`);
      }
    } catch (err) {
      setMessage('❌ Server error');
    }
  };

  return (
    <div className="register-page">
      <img src={require('../assets/chitconnect-logo-blue.png')} alt="ChitConnect Logo" style={{ maxWidth: '200px', marginBottom: '20px' }} />
      <h2>Register to ChitConnect</h2>

      <form onSubmit={handleSubmit} className="register-form">
        <label>
          Given Name(s):
          <input type="text" name="given_name" value={form.given_name} onChange={handleChange} />
          {errors.given_name && <small className="error">{errors.given_name}</small>}
        </label>

        <label>
          Surname:
          <input type="text" name="surname" value={form.surname} onChange={handleChange} />
          {errors.surname && <small className="error">{errors.surname}</small>}
        </label>

        <label>
          Email:
          <input type="email" name="email" value={form.email} onChange={handleChange} />
          {errors.email && <small className="error">{errors.email}</small>}
        </label>

        <label>
          Mobile:
          <input type="text" name="mobile" value={form.mobile} onChange={handleChange} />
          {errors.mobile && <small className="error">{errors.mobile}</small>}
        </label>

        <AddressAutofill accessToken={process.env.REACT_APP_MAPBOX_TOKEN} onRetrieve={handleAddressSelect}>
          <label>
            Street Address:
            <input type="text" name="address" autoComplete="address-line1" value={form.address} onChange={handleChange} />
          </label>

          <label>
            City:
            <input type="text" name="city" autoComplete="address-level2" value={form.city} onChange={handleChange} />
          </label>

          <label>
            State:
            <input type="text" name="state" autoComplete="address-level1" value={form.state} onChange={handleChange} />
          </label>

          <label>
            Postcode:
            <input type="text" name="postcode" autoComplete="postal-code" value={form.postcode} onChange={handleChange} />
          </label>

          <label>
            Country:
            <input type="text" name="country" autoComplete="country" value={form.country} onChange={handleChange} style={{ textTransform: 'uppercase' }} />
          </label>
        </AddressAutofill>

        <label>
          Password:
          <input type="password" name="password" value={form.password} onChange={handleChange} className="password-input" />
          {form.password && (
            <>
              <div className={`strength-bar ${passwordStrength().toLowerCase()}`}></div>
              <small className="strength-label">Strength: {passwordStrength()}</small>
            </>
          )}
          {errors.password && <small className="error">{errors.password}</small>}
        </label>

        <label>
          Confirm Password:
          <input type="password" name="confirmPassword" value={form.confirmPassword} onChange={handleChange} className="password-input" />
          {errors.confirmPassword && <small className="error">{errors.confirmPassword}</small>}
        </label>

        <button type="submit">Register</button>
        {message && <p className="register-message">{message}</p>}

        <div style={{ marginTop: '20px', fontStyle: 'italic', color: '#555' }}>
          Detected Time Zone: {form.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone}
        </div>
      </form>

      <p style={{ marginTop: '20px' }}>
        Already have an account? <Link to="/login">Login here</Link>
      </p>
    </div>
  );
}
